/**
 * Comptes du back-office : liste, création, mise à jour, rôles, classes affectées, profil du compte connecté.
 *
 * Extrait de l'ancien services/api.ts (Lot 8, déplacement pur).
 */

import { getAuth } from 'firebase/auth';
import {
  AdminUser,
  AdminUserFormData,
  UserListResponse,
  AvailableRolesResponse,
  CreateUserResponse,
} from '../types';
import type { ApiResponse } from '../types/api';
import { ADMIN_URLS, USER_PATHS } from '../config/adminApi';
import { createApiClient } from '../lib/http';

const api = createApiClient();

export class AdminUsersApiService {
  /**
   * Get list of admin users
   * @param params Query parameters (role, status, limit, search)
   */
  static async getAdminUsers(params?: {
    role?: string;
    status?: string;
    limit?: number;
    search?: string;
  }): Promise<UserListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.role) queryParams.append('role', params.role);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get<UserListResponse>(`${ADMIN_URLS.users}?${queryParams.toString()}`);
    return response.data;
  }

  /**
   * Get a specific admin user by ID
   */
  static async getAdminUser(userId: string): Promise<ApiResponse<AdminUser>> {
    const response = await api.get<ApiResponse<AdminUser>>(`${ADMIN_URLS.users}/${userId}`);
    return response.data;
  }

  /**
   * Create a new admin user
   */
  static async createAdminUser(userData: AdminUserFormData): Promise<CreateUserResponse> {
    const response = await api.post<CreateUserResponse>(`${ADMIN_URLS.users}`, userData);
    return response.data;
  }

  /**
   * Update an admin user
   */
  static async updateAdminUser(
    userId: string,
    updates: Partial<AdminUserFormData>
  ): Promise<ApiResponse<AdminUser>> {
    const response = await api.put<ApiResponse<AdminUser>>(`${ADMIN_URLS.users}/${userId}`, updates);
    return response.data;
  }

  /**
   * Delete an admin user (super_admin only)
   */
  static async deleteAdminUser(userId: string): Promise<ApiResponse<void>> {
    const response = await api.delete<ApiResponse<void>>(`${ADMIN_URLS.users}/${userId}`);
    return response.data;
  }

  /**
   * Assign classes to a user (lecteur role)
   */
  static async assignClassesToUser(
    userId: string,
    classIds: string[]
  ): Promise<ApiResponse<AdminUser>> {
    const response = await api.put<ApiResponse<AdminUser>>(`${ADMIN_URLS.users}/${userId}/classes`, { classIds });
    return response.data;
  }

  /**
   * Toggle read-only mode for a user
   * When enabled, the user cannot modify any resources
   */
  static async toggleUserReadOnly(
    userId: string,
    isReadOnly: boolean
  ): Promise<ApiResponse<AdminUser>> {
    const response = await api.put<ApiResponse<AdminUser>>(`${ADMIN_URLS.users}/${userId}/read-only`, { isReadOnly });
    return response.data;
  }

  /**
   * Toggle AI correction access for a user (super_admin only)
   * When enabled, the user can use AI-assisted correction features
   */
  static async toggleUserAICorrection(
    userId: string,
    canUseAICorrection: boolean
  ): Promise<ApiResponse<AdminUser>> {
    const response = await api.put<ApiResponse<AdminUser>>(`${ADMIN_URLS.users}/${userId}/ai-correction`, { canUseAICorrection });
    return response.data;
  }

  /**
   * Get the current user's profile (including canUseAICorrection flag)
   */
  static async getMyProfile(): Promise<ApiResponse<AdminUser>> {
    const response = await api.get<ApiResponse<AdminUser>>(USER_PATHS.me);
    return response.data;
  }

  /**
   * Get available roles that current user can manage
   */
  static async getAvailableRoles(): Promise<AvailableRolesResponse> {
    const response = await api.get<AvailableRolesResponse>(`${ADMIN_URLS.users}/roles`);
    return response.data;
  }

  /**
   * Refresh user custom claims (force token refresh)
   */
  static async refreshUserClaims(): Promise<void> {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      // Force token refresh to get updated custom claims
      await user.getIdToken(true);
    }
  }
}

export default AdminUsersApiService;
